/**
 * Entity mutation primitives — `upsertEntity` and `deleteEntityLocal`.
 *
 * These are the canonical, shim-free helpers that:
 *   1. Normalize a `BaseEntityType` (timestamps, mirrored `type`/`entityType`,
 *      Doc base64/content stripping for data-loss prevention).
 *   2. Dispatch to `entityRegistry`, which routes the entity to its owning
 *      scope (spaceEntityStore / globalEntityStore / app store) by type.
 *
 * They do *not* talk to the network — `entityService.*` is the canonical REST
 * surface — and they do *not* maintain undo/redo. Higher-level orchestration
 * (optimistic create/update/delete with REST + rollback) lives in
 * `entityActions.ts`, which sits on top of these primitives.
 *
 * Callers that previously did `upsertEntity(...)`
 * should call `upsertEntity(...)` from this module instead.
 */

import type { BaseEntityType } from '../types';
import { entityRegistry } from './entityRegistry';
import { useEntityByIdCache } from './entityByIdCache';

export interface MutationOptions {
  /** Source of the mutation. Used to enforce the no-CRDT-loop invariant. */
  source?: 'local' | 'crdt' | 'event';
  /**
   * Whether the mutation should also be broadcast to remote peers. We keep
   * this argument so existing call sites read identically; the real broadcast
   * is owned by `entitySystemYjsStore` / orgYDocStore observers, not this
   * module. CRDT-origin mutations must pass `syncCRDT: false`.
   */
  syncCRDT?: boolean;
  /**
   * Legacy undo/redo logging knob. Retained as a no-op for back-compat with
   * existing call sites — the entity-store-level undo stack has been retired
   * (apps that need undo use Y.js UndoManager directly).
   */
  log?: boolean;
}

/**
 * Normalize an entity exactly the way the legacy `_applyLocalUpsert` did and
 * dispatch it through the registry.
 *
 * Returns the normalized entity so callers can read back the canonical shape
 * (e.g. defaulted timestamps).
 */
export function upsertEntity(entity: BaseEntityType, opts?: MutationOptions): BaseEntityType {
  if (!entity || (!entity._id && !(entity as any).id)) {
    throw new Error(
      `[entityMutations] upsertEntity called with missing _id: ${entity?.name} (${entity?.entityType})`,
    );
  }

  const options: Required<MutationOptions> = {
    source: 'local',
    syncCRDT: true,
    log: true,
    ...opts,
  };

  if (options.source === 'crdt' && options.syncCRDT !== false) {
    throw new Error(
      `[entityMutations] CRDT loop prevented: CRDT-origin mutations must set syncCRDT=false. Entity: ${entity._id}`,
    );
  }

  let normalized: BaseEntityType = {
    ...entity,
    created_at: entity.created_at || new Date().toISOString(),
    updated_at: entity.updated_at || new Date().toISOString(),
    type: entity.type || entity.entityType,
    entityType: entity.entityType || entity.type,
  } as BaseEntityType;

  // Doc bodies are heavy and stored separately (Y.js or REST); never cache the
  // base64 inline content in the entity registry, otherwise stale snapshots
  // can clobber active edits on the next read.
  if (entity.entityType === 'Doc' && entity.skeleton) {
    normalized = {
      ...normalized,
      skeleton: { ...entity.skeleton, base64: undefined, content: undefined },
      __fromCache: true,
    } as any;
  }

  entityRegistry.upsert(normalized as any);

  // Keep useEntityByIdCache in sync so that useEntityById (and therefore
  // useWindowTitle in WindowContainer) re-renders immediately when an entity
  // is renamed or otherwise mutated — without waiting for a REST re-fetch.
  //
  // Only update for local mutations (user action or REST confirmation), NOT for
  // crdt-source mutations. CRDT-loaded entities may carry stale names (e.g. when
  // the entity was renamed via REST but the CRDT hasn't synced yet). Putting a
  // stale CRDT entity into the cache would prevent useEntityById from ever
  // triggering its own REST fetch to get the authoritative name.
  //
  // persist: true — write the updated entity to the entity-by-id IndexedDB so the
  // correct name survives page refresh. Without this, the IDB retains the name from
  // the most recent REST fetch (which may be stale), causing a flash of the old title
  // until the REST fetch completes on the next load.
  if (options.source !== 'crdt') {
    // Skip IDB persistence for content-stripped entities (__fromCache: true).
    // These have Doc base64/content removed and must never overwrite the full
    // entity that may already be in the IDB from a prior REST fetch. The name
    // field is still correct and will be picked up from the in-memory registry
    // by subscribeToChanges; the IDB is only needed for cross-session title
    // persistence, which is served by the full entity written at REST-fetch time.
    const shouldPersist = !(normalized as any).__fromCache;
    useEntityByIdCache.getState().put(normalized, { persist: shouldPersist });
  }

  return normalized;
}

/**
 * Remove an entity from the registry. When `entityType` is provided we route
 * directly to the owning scope; otherwise we fan out to all scopes (used by
 * Y.Doc tombstones where the type is no longer available).
 */
export function deleteEntityLocal(id: string, entityTypeOrOpts?: string | MutationOptions, _opts?: MutationOptions): void {
  if (!id) return;
  const entityType =
    typeof entityTypeOrOpts === 'string' ? entityTypeOrOpts : undefined;
  // Look the entity up first when the caller didn't tell us the type, so the
  // registry can route to a single scope rather than fanning out.
  const resolvedType =
    entityType ?? entityRegistry.getById(id)?.entityType ?? undefined;
  entityRegistry.deleteEntity(id, resolvedType);
}
